"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../../lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'

export interface WidgetBaseProps {
  title?: string
  subtitle?: string
  children: React.ReactNode
  variant?: 'default' | 'minimal' | 'glass' | 'bordered' | 'gradient' | 'colored' | 'overlay' | 'floating'
  backgroundVariant?: 'default' | 'minimal' | 'glass' | 'bordered' | 'gradient' | 'colored' | 'overlay' | 'floating'
  colorScheme?: 'blue' | 'green' | 'purple' | 'orange' | 'pink' | 'cyan' | 'neutral'
  className?: string
  headerAction?: React.ReactNode
  loading?: boolean
  error?: string | null
  animate?: boolean
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  gradientDirection?: 'to-t' | 'to-tr' | 'to-r' | 'to-br' | 'to-b' | 'to-bl' | 'to-l' | 'to-tl'
  overlay?: {
    pattern?: 'dots' | 'grid' | 'diagonal' | 'waves' | 'none'
    opacity?: number
    color?: string
  }
}

export function WidgetBase({
  title,
  subtitle,
  children,
  variant = 'default',
  backgroundVariant,
  colorScheme = 'blue',
  className,
  headerAction,
  loading = false,
  error = null,
  animate = true,
  rounded = 'lg',
  shadow = 'sm',
  gradientDirection = 'to-br',
  overlay
}: WidgetBaseProps) {
  // Use backgroundVariant if provided, otherwise fall back to variant
  const activeVariant = backgroundVariant || variant
  // Color scheme classes for gradient variant (subtle)
  const colorSchemeClasses = {
    blue: 'from-blue-500/10 via-blue-500/5 to-transparent border-blue-500/20 dark:from-blue-400/10 dark:via-blue-400/5 dark:border-blue-400/20',
    green: 'from-green-500/10 via-green-500/5 to-transparent border-green-500/20 dark:from-green-400/10 dark:via-green-400/5 dark:border-green-400/20',
    purple: 'from-purple-500/10 via-purple-500/5 to-transparent border-purple-500/20 dark:from-purple-400/10 dark:via-purple-400/5 dark:border-purple-400/20',
    orange: 'from-orange-500/10 via-orange-500/5 to-transparent border-orange-500/20 dark:from-orange-400/10 dark:via-orange-400/5 dark:border-orange-400/20',
    pink: 'from-pink-500/10 via-pink-500/5 to-transparent border-pink-500/20 dark:from-pink-400/10 dark:via-pink-400/5 dark:border-pink-400/20',
    cyan: 'from-cyan-500/10 via-cyan-500/5 to-transparent border-cyan-500/20 dark:from-cyan-400/10 dark:via-cyan-400/5 dark:border-cyan-400/20',
    neutral: 'from-gray-500/10 via-gray-500/5 to-transparent border-gray-500/20 dark:from-gray-400/10 dark:via-gray-400/5 dark:border-gray-400/20'
  }

  // Color scheme classes for colored variant (more vibrant)
  const colorSchemeClassesColored = {
    blue: 'from-blue-500/20 via-blue-500/10 to-transparent border-blue-500/30 dark:from-blue-400/20 dark:via-blue-400/10 dark:border-blue-400/30',
    green: 'from-green-500/20 via-green-500/10 to-transparent border-green-500/30 dark:from-green-400/20 dark:via-green-400/10 dark:border-green-400/30',
    purple: 'from-purple-500/20 via-purple-500/10 to-transparent border-purple-500/30 dark:from-purple-400/20 dark:via-purple-400/10 dark:border-purple-400/30',
    orange: 'from-orange-500/20 via-orange-500/10 to-transparent border-orange-500/30 dark:from-orange-400/20 dark:via-orange-400/10 dark:border-orange-400/30',
    pink: 'from-pink-500/20 via-pink-500/10 to-transparent border-pink-500/30 dark:from-pink-400/20 dark:via-pink-400/10 dark:border-pink-400/30',
    cyan: 'from-cyan-500/20 via-cyan-500/10 to-transparent border-cyan-500/30 dark:from-cyan-400/20 dark:via-cyan-400/10 dark:border-cyan-400/30',
    neutral: 'from-gray-500/20 via-gray-500/10 to-transparent border-gray-500/30 dark:from-gray-400/20 dark:via-gray-400/10 dark:border-gray-400/30'
  }

  // Rounded classes
  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md', 
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    full: 'rounded-full'
  }

  // Shadow classes
  const shadowClasses = {
    none: 'shadow-none',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    '2xl': 'shadow-2xl'
  }

  // Overlay patterns
  const overlayPatterns = {
    dots: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
    grid: 'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)',
    diagonal: 'repeating-linear-gradient(45deg, transparent, transparent 2px, currentColor 2px, currentColor 4px)',
    waves: 'radial-gradient(ellipse at center, transparent 20%, currentColor 21%, currentColor 25%, transparent 26%)',
    none: 'none'
  }

  // Gradient direction classes
  const gradientDirectionClasses = {
    'to-t': 'bg-gradient-to-t',     // top
    'to-tr': 'bg-gradient-to-tr',   // top-right
    'to-r': 'bg-gradient-to-r',     // right
    'to-br': 'bg-gradient-to-br',   // bottom-right (default)
    'to-b': 'bg-gradient-to-b',     // bottom
    'to-bl': 'bg-gradient-to-bl',   // bottom-left
    'to-l': 'bg-gradient-to-l',     // left
    'to-tl': 'bg-gradient-to-tl'    // top-left
  }

  // Gradient direction styles for inline application
  const gradientDirectionStyles = {
    'to-t': { backgroundImage: 'linear-gradient(to top, var(--tw-gradient-stops))' },
    'to-tr': { backgroundImage: 'linear-gradient(to top right, var(--tw-gradient-stops))' },
    'to-r': { backgroundImage: 'linear-gradient(to right, var(--tw-gradient-stops))' },
    'to-br': { backgroundImage: 'linear-gradient(to bottom right, var(--tw-gradient-stops))' },
    'to-b': { backgroundImage: 'linear-gradient(to bottom, var(--tw-gradient-stops))' },
    'to-bl': { backgroundImage: 'linear-gradient(to bottom left, var(--tw-gradient-stops))' },
    'to-l': { backgroundImage: 'linear-gradient(to left, var(--tw-gradient-stops))' },
    'to-tl': { backgroundImage: 'linear-gradient(to top left, var(--tw-gradient-stops))' }
  }

  // Variant classes - floating efekti ve gradient yönlendirme düzeltmeleri
  const variantClasses = {
    default: 'bg-card border',
    minimal: 'border-0 shadow-none bg-card/50',
    glass: 'bg-background/60 backdrop-blur-md border-white/10',
    bordered: 'border-2 bg-card',
    gradient: `${gradientDirectionClasses[gradientDirection]} ${colorSchemeClasses[colorScheme]}`,
    colored: `${gradientDirectionClasses[gradientDirection]} ${colorSchemeClassesColored[colorScheme]} border-2`,
    overlay: 'bg-card border relative overflow-hidden',
    floating: 'bg-card border-0 shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 ring-1 ring-gray-200/50 dark:ring-gray-700/50'
  }

  const content = (
    <Card 
      className={cn(
        variantClasses[activeVariant],
        roundedClasses[rounded],
        shadowClasses[shadow],
        'transition-all duration-200 relative',
        className
      )}
      style={(activeVariant === 'gradient' || activeVariant === 'colored') ? gradientDirectionStyles[gradientDirection] : undefined}
    >
      {/* Overlay pattern */}
      {(activeVariant === 'overlay' || overlay) && overlay?.pattern && overlay.pattern !== 'none' && (
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: overlayPatterns[overlay.pattern],
            backgroundSize: overlay.pattern === 'dots' ? '8px 8px' : 
                           overlay.pattern === 'grid' ? '20px 20px' :
                           overlay.pattern === 'diagonal' ? '8px 8px' :
                           overlay.pattern === 'waves' ? '60px 60px' : 'auto',
            opacity: overlay.opacity || 0.1,
            color: overlay.color || 'currentColor'
          }}
        />
      )}
      {(title || subtitle || headerAction) && (
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            {title && (
              <CardTitle className="text-base font-semibold">
                {title}
              </CardTitle>
            )}
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-1">
                {subtitle}
              </p>
            )}
          </div>
          {headerAction && (
            <div className="ml-4">
              {headerAction}
            </div>
          )}
        </CardHeader>
      )}
      
      <CardContent className={cn(
        (title || subtitle || headerAction) ? "pt-0" : "pt-6"
      )}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse space-y-2">
              <div className="h-4 bg-muted rounded w-24 mx-auto" />
              <div className="h-8 bg-muted rounded w-16 mx-auto" />
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8 text-center">
            <div className="text-destructive">
              <p className="text-sm font-medium">Error</p>
              <p className="text-xs text-muted-foreground mt-1">{error}</p>
            </div>
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  )

  if (animate) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        {content}
      </motion.div>
    )
  }

  return content
}

export default WidgetBase